import {Color} from '@benchling/nucleus-ui';
import {Divider, Flex, SimpleGrid, Text} from '@mantine/core';
import gql from 'graphql-tag';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {MoleculeFormDetailsQuery} from 'chemistry/molecules/tabs/__generated/MoleculeFormDetailsQuery';
import {MoleculeFieldLabel, MoleculeFieldValue} from 'chemistry/molecules/tabs/MoleculeStructureField';
import {MoleculeSchemaInterfaceApiId} from 'chemistry/schema-configuration/MoleculeSchemaInterfaceConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export const MISSING_SALT_OR_SOLVENT_POPOVER =
  'This molecule is missing a salt or solvate. Apply a different schema, or edit the molecular structure to register.';

export const UNEXPECTED_FRAGMENT_POPOVER =
  'The schema you applied does not account for this fragment. Change the schema, or remove the fragment to register.';

export const MOLECULE_FORM_DETAILS_QUERY = gql`
  query MoleculeFormDetailsQuery($moleculeId: StrictString!, $moleculeVersionId: StrictString!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        tagSchema {
          id
          interfaces {
            ... on StaticSchemaInterfaceNode {
              id
            }
          }
        }
        version(versionId: $moleculeVersionId) {
          id
          chemicalStructure {
            id
            salts {
              ratioNumerator
              ratioDenominator
              salt {
                id
                name
              }
            }
            solvents {
              ratioNumerator
              ratioDenominator
              solvent {
                id
                name
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface MoleculeFormDetailsProps {
  moleculeId: string;
  moleculeVersionId: string;
}

function WarningIcon({popoverText, altText}: {popoverText: string; altText: string}): JSX.Element {
  return (
    <Popover placement={FloatingPlacement.TOP} renderContent={() => popoverText}>
      <BenchlingIcon icon="exclamation" color={Color.RED_500} altText={altText} />
    </Popover>
  );
}

export default function MoleculeFormDetails(props: MoleculeFormDetailsProps): JSX.Element | null {
  if (!BenchlingConfig.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE) {
    return null;
  }

  return (
    <BenchlingQuery
      query={MOLECULE_FORM_DETAILS_QUERY}
      variables={{moleculeId: props.moleculeId, moleculeVersionId: props.moleculeVersionId}}
      renderLoading={() => <></>}
    >
      {(data: MoleculeFormDetailsQuery) => {
        if (isInaccessible(data.molecule) || !data.molecule.version) {
          return <></>;
        }

        const {salts, solvents} = data.molecule.version.chemicalStructure;
        const hasSaltsOrSolvents = salts.length > 0 || solvents.length > 0;

        const implementedInterfaceIds = new Set(
          (data.molecule.tagSchema?.interfaces ?? []).flatMap((iface) =>
            iface.__typename === 'StaticSchemaInterfaceNode' ? [iface.id] : []
          )
        );
        const isFormSSI = implementedInterfaceIds.has(MoleculeSchemaInterfaceApiId.MOLECULE_FORM_ID);
        const isCompoundSSI = implementedInterfaceIds.has(MoleculeSchemaInterfaceApiId.MOLECULE_COMPOUND_ID);

        // If there are no salts/solvents AND the molecule does not implement the Form SSI, render nothing.
        if (!isFormSSI && !hasSaltsOrSolvents) {
          return <></>;
        }

        // If the molecule implements the Compound SSI and there are salts/solvents, then show a warning about unexpected fragments.
        const showUnexpectedFragmentWarning = isCompoundSSI && hasSaltsOrSolvents;

        const counterparts = [
          ...salts.map((c) => ({
            id: c.salt.id,
            name: c.salt.name,
            ratio: `${c.ratioNumerator}:${c.ratioDenominator}`,
          })),
          ...solvents.map((c) => ({
            id: c.solvent.id,
            name: c.solvent.name,
            ratio: `${c.ratioNumerator}:${c.ratioDenominator}`,
          })),
        ];

        let cells: Array<JSX.Element> = [];
        if (isFormSSI && !hasSaltsOrSolvents) {
          cells = [
            <MoleculeFieldValue key="empty-name-cell">
              <Flex justify="space-between" wrap="nowrap" w="100%">
                <Text span={true} fs="italic">
                  No salts or solvates detected
                </Text>
                <WarningIcon
                  popoverText={MISSING_SALT_OR_SOLVENT_POPOVER}
                  altText="Details about missing salt or solvate"
                />
              </Flex>
            </MoleculeFieldValue>,
            <MoleculeFieldValue key="empty-ratio-cell">{null}</MoleculeFieldValue>,
          ];
        } else {
          cells = counterparts.flatMap((counterpart) => [
            <MoleculeFieldValue
              key={`name-${counterpart.id}`}
              {...getTestProps({
                component: 'MoleculeFormDetails',
                element: 'counterpartName',
                key: `name-${counterpart.id}`,
              })}
              data-amp-safe={false}
            >
              <Flex justify="space-between" wrap="nowrap" w="100%">
                <Text span={true}>{counterpart.name}</Text>
                {showUnexpectedFragmentWarning && (
                  <WarningIcon
                    popoverText={UNEXPECTED_FRAGMENT_POPOVER}
                    altText="Warning about unexpected fragment"
                  />
                )}
              </Flex>
            </MoleculeFieldValue>,
            <MoleculeFieldValue
              key={`ratio-${counterpart.id}`}
              {...getTestProps({
                component: 'MoleculeFormDetails',
                element: 'counterpartRatio',
                key: `ratio-${counterpart.id}`,
              })}
              data-amp-safe={false}
            >
              <Text span={true}>{counterpart.ratio}</Text>
            </MoleculeFieldValue>,
          ]);
        }

        return (
          <div {...getTestProps({component: 'MoleculeFormDetails', element: 'root'})}>
            <Divider mt="lg" size="2px" color={Color.GRAY_300} />
            <SimpleGrid cols={2} mt="lg" spacing={12}>
              <MoleculeFieldLabel>Salts or Solvates</MoleculeFieldLabel>
              <MoleculeFieldLabel>Stoichiometric Ratio</MoleculeFieldLabel>
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={12}>
              {cells}
            </SimpleGrid>
          </div>
        );
      }}
    </BenchlingQuery>
  );
}
